import { useMemo, useRef, useCallback } from 'react';
import useResponsiveValue from '../../../hooks/useResponsiveValue.js';
import { usePaginationContext } from './usePaginationContext.js';
import { range } from '../util.js';
import '../../../hooks/useBreakpointIndex.js';
import '@emotion/react';
import '../PaginationContext.js';

const MIN_ITEMS_PER_PAGE = 5;
const DEFAULT_MAX_ITEMS_PER_PAGE = 7;
const getPageItem = _ref => {
  let {
    pageNumber,
    activePage
  } = _ref;
  return {
    type: "page",
    isActive: pageNumber === activePage,
    pageNumber
  };
};
const usePaginationList = _ref2 => {
  let {
    maxVisiblePageItemsProp,
    mobileProp
  } = _ref2;
  const mobileDefault = useResponsiveValue([true, false]);
  const responsiveMobileProp = useResponsiveValue(mobileProp);
  const mobile = responsiveMobileProp ?? mobileDefault;
  const maxVisiblePageItemsDefault = useResponsiveValue([MIN_ITEMS_PER_PAGE, DEFAULT_MAX_ITEMS_PER_PAGE]);
  const maxVisiblePageItemsPropValue = useResponsiveValue(maxVisiblePageItemsProp);
  const paginationContext = usePaginationContext();
  const {
    activePage,
    totalPages,
    handlePageChange,
    setPageLinkProps
  } = paginationContext ?? {};
  const maxVisiblePageItems = Math.max(maxVisiblePageItemsPropValue ?? maxVisiblePageItemsDefault, MIN_ITEMS_PER_PAGE);
  const visiblePageItems = totalPages !== undefined ? Math.min(totalPages, maxVisiblePageItems) : maxVisiblePageItems;

  // Create an array of items to represent page numbers and ellipses
  const paginationItems = useMemo(() => {
    // Make sure context values are present. If not, return an empty array.
    if (totalPages === undefined || activePage === undefined || visiblePageItems === null) return [];
    const ellipsisItem = {
      type: "ellipsis"
    };

    // Show all page numbers if totalPages is less than or equal to visiblePageItems
    if (visiblePageItems >= totalPages) {
      return range(1, totalPages).map(pageNumber => getPageItem({
        pageNumber,
        activePage
      }));
    }
    const leftEllipsisThreshold = visiblePageItems - Math.floor(visiblePageItems / 2);
    const showLeftEllipsis = activePage > leftEllipsisThreshold;
    const rightEllipsisThreshold = totalPages - (visiblePageItems - Math.ceil(visiblePageItems / 2));
    const showRightEllipsis = activePage < rightEllipsisThreshold;

    // No left dots to show, but rights dots to be shown
    if (!showLeftEllipsis && showRightEllipsis) {
      const leftRange = range(1, visiblePageItems - 2);
      return [...leftRange.map(pageNumber => getPageItem({
        pageNumber,
        activePage
      })), ellipsisItem, getPageItem({
        pageNumber: totalPages
      })];
    }

    //  No right dots to show, but left dots to be shown
    if (showLeftEllipsis && !showRightEllipsis) {
      const rightItemCount = visiblePageItems - 2;
      const rightRange = range(totalPages - rightItemCount + 1, totalPages);
      return [getPageItem({
        pageNumber: 1
      }), ellipsisItem, ...rightRange.map(pageNumber => getPageItem({
        pageNumber,
        activePage
      }))];
    }

    // Both left and right dots to be shown
    const remainingItemCount = visiblePageItems - 5;
    const rangeStart = Math.ceil(activePage - remainingItemCount / 2);
    const rangeEnd = Math.ceil(activePage + remainingItemCount / 2);
    const middleRange = range(rangeStart, rangeEnd);
    return [getPageItem({
      pageNumber: 1
    }), ellipsisItem, ...middleRange.map(pageNumber => getPageItem({
      pageNumber,
      activePage
    })), ellipsisItem, getPageItem({
      pageNumber: totalPages
    })];
  }, [visiblePageItems, activePage, totalPages]);
  const firstPageButtonRef = useRef(null);
  const lastPageButtonRef = useRef(null);
  const handlePrevClick = useCallback(willBecomeDisabled => {
    if (willBecomeDisabled && firstPageButtonRef.current) {
      firstPageButtonRef.current.focus();
    }
  }, []);
  const handleNextClick = useCallback(willBecomeDisabled => {
    if (willBecomeDisabled && lastPageButtonRef.current) {
      lastPageButtonRef.current.focus();
    }
  }, []);
  return {
    paginationContext,
    mobile,
    paginationItems,
    handlePageChange,
    setPageLinkProps,
    firstPageButtonRef,
    lastPageButtonRef,
    handlePrevClick,
    handleNextClick,
    totalPages
  };
};

export { usePaginationList };
